import { pubsub } from '@services';
import { pushDataLayer } from '@utils';

/**
 * LocalizationForm - Handles dynamic language filtering based on country selection
 * @description Updates available languages when a country is selected in the localization form
 */
class LocalizationForm extends HTMLElement {
  /** @type {HTMLElement|null} Country content-select element (#localization-country) */
  #countrySelect = null;
  /** @type {HTMLElement|null} Language content-select element (#localization-language) */
  #languageSelect = null;
  /** @type {HTMLFormElement|null} Localization form element */
  #form = null;
  /** @type {string|null} Source country ISO when modal opened / previous selection for GTM countryChange */
  #lastCountryValue = null;
  /** @type {(() => void)|null} Unsubscribe from pubsub when dialog opened */
  #unsubscribeDialogOpened = null;

  connectedCallback() {
    // Find selects by their IDs
    this.#countrySelect = this.querySelector('[data-localization-country]');
    this.#languageSelect = this.querySelector('[data-localization-language]');

    if (!this.#countrySelect || !this.#languageSelect) {
      return;
    }

    this.#form = this.querySelector('form');

    this.#setupEventListeners();
    this.#setupGtmCountryEvents();
    // Initialize language options based on current country
    this.#filterLanguageOptions();
    this.#setActiveLanguageOnDialogOpened();
    // Setup form submission handler for custom URLs
    this.#setupFormSubmission();
  }

  //set the active language based on the current language and the weglot language
  #setActiveLanguage() {
    const currentLanguage = this.#languageSelect.querySelector('[data-select-input]').value;
      if ((currentLanguage !== window.Weglot.getCurrentLang()) && window.Weglot) {
        //select the button with the new language
        const newLanguageButton = this.#languageSelect.querySelector(`[data-value="${window.Weglot.getCurrentLang()}"]`);
        if (newLanguageButton) {
          this.#selectLanguageOption(newLanguageButton);
        }
    }
  }
  

  //when dialog is opened, set the active language if weglot is initialized. Otherwise, leave as set by liquid localization.
  #setActiveLanguageOnDialogOpened() {
    this.#unsubscribeDialogOpened = pubsub.subscribe('localization-dialog:opened', () => {
      if (window.Weglot) {     
        this.#setActiveLanguage();
      }
    });
  }



  /**
   * Get current country ISO code from the country select input
   * @returns {string|null}
   */
  #getCountryValue() {
    const countryInput = this.#countrySelect?.querySelector('[data-select-input]');
    return countryInput?.value ?? null;
  }

  /**
   * Setup GTM dataLayer events for country selector: capture source when modal opens, push countryChange on selection
   */
  #setupGtmCountryEvents() {
    const countryInput = this.#countrySelect.querySelector('[data-select-input]');
    if (!countryInput) return;

    this.#unsubscribeDialogOpened = pubsub.subscribe('localization-dialog:opened', () => {
      this.#lastCountryValue = this.#getCountryValue();
    });

    countryInput.addEventListener('change', () => {
      const newCountry = this.#getCountryValue();
      const initialCountry = this.#lastCountryValue;
      if (newCountry && initialCountry !== newCountry) {
        pushDataLayer({
          event: 'countryChange',
          initialCountry: initialCountry ?? undefined,
          newCountry,
        });
      }
      this.#lastCountryValue = newCountry;
    });
  }

  disconnectedCallback() {
    this.#unsubscribeDialogOpened?.();
  }

  /**
   * Setup event listeners for country selection changes
   */
  #setupEventListeners() {
    // Listen for changes on the hidden input of the country select
    const countryInput = this.#countrySelect.querySelector('[data-select-input]');
    if (countryInput) {
      countryInput.addEventListener('change', () => {
        this.#filterLanguageOptions();
      });
    }
  }

  /**
   * Get the data-country-language-map value from the selected country option
   * @returns {string|null} The country-language-map value or null if not found
   */
  #getSelectedCountryLanguageMap() {
    const countryList = this.#countrySelect.querySelector('[data-select-list]');
    if (!countryList) return null;

    // Find the selected country option by data-selected attribute (more reliable than value)
    const selectedCountryOption = countryList.querySelector('[role="menuitem"][data-selected]');

    // Fallback: if no data-selected, try to find by input value
    if (!selectedCountryOption) {
      const countryInput = this.#countrySelect.querySelector('[data-select-input]');
      if (countryInput && countryInput.value) {
        const selectedCountryValue = countryInput.value;
        const optionByValue = Array.from(countryList.querySelectorAll('[role="menuitem"]')).find(
          (option) => option.dataset.value === selectedCountryValue
        );

        return optionByValue?.dataset.countryLanguageMap || null;
      }
      return null;
    }

    return selectedCountryOption.dataset.countryLanguageMap || null;
  }

  /**
   * Filter language options based on selected country's data-country-language-map value
   */
  #filterLanguageOptions() {
    const selectedCountryLanguageMap = this.#getSelectedCountryLanguageMap();

    // If no mapping found, show all languages
    if (!selectedCountryLanguageMap) {
      const languageList = this.#languageSelect.querySelector('[data-select-list]');
      if (languageList) {
        const languageOptions = languageList.querySelectorAll('[role="menuitem"]');
        languageOptions.forEach((option) => {
          option.classList.remove('hidden');
        });
      }
      return;
    }

    const languageList = this.#languageSelect.querySelector('[data-select-list]');
    if (!languageList) return;

    const { visibleCount, firstVisibleOption } = this.#updateLanguageVisibility(
      languageList,
      selectedCountryLanguageMap
    );

    this.#handleLanguageSelection(firstVisibleOption, visibleCount);
  }

  /**
   * Update visibility of language options based on matching data-country-language-map
   * @param {HTMLElement} languageList - The language list container
   * @param {string} selectedCountryLanguageMap - The country-language-map value to match
   * @returns {Object} Object containing visibleCount and firstVisibleOption
   */
  #updateLanguageVisibility(languageList, selectedCountryLanguageMap) {
    const languageOptions = languageList.querySelectorAll('[role="menuitem"]');
    let visibleCount = 0;
    let firstVisibleOption = null;

    languageOptions.forEach((option) => {
      const optionLanguageMap = option.dataset.countryLanguageMap;
      const isAvailable = optionLanguageMap === selectedCountryLanguageMap;

      if (isAvailable) {
        option.classList.remove('hidden');
        visibleCount++;
        if (!firstVisibleOption) {
          firstVisibleOption = option;
        }
      } else {
        option.classList.add('hidden');
      }
    });

    return { visibleCount, firstVisibleOption };
  }

  /**
   * Check if current language is still available for the selected country
   * @param {string} selectedCountryLanguageMap - The country-language-map value
   * @returns {boolean} True if current language is available
   */
  #isCurrentLanguageAvailable(selectedCountryLanguageMap) {
    const languageInput = this.#languageSelect.querySelector('[data-select-input]');
    const currentLanguage = languageInput?.value;
    if (!currentLanguage) return false;

    const languageList = this.#languageSelect.querySelector('[data-select-list]');
    if (!languageList) return false;

    const currentLanguageOption = Array.from(
      languageList.querySelectorAll('[role="menuitem"]')
    ).find(
      (option) =>
        option.dataset.value === currentLanguage &&
        option.dataset.countryLanguageMap === selectedCountryLanguageMap
    );

    return currentLanguageOption?.dataset.countryLanguageMap === selectedCountryLanguageMap;
  }

  /**
   * Handle language selection based on availability
   * @param {HTMLElement} firstVisibleOption - The first visible language option
   * @param {number} visibleCount - Number of visible language options
   */
  #handleLanguageSelection(firstVisibleOption, visibleCount) {
    const selectedCountryLanguageMap = this.#getSelectedCountryLanguageMap();
    if (!selectedCountryLanguageMap || !firstVisibleOption) return;

    // Check if currently selected language is still available
    const isCurrentLanguageAvailable = this.#isCurrentLanguageAvailable(selectedCountryLanguageMap);
    if (!isCurrentLanguageAvailable) {
      this.#selectLanguageOption(firstVisibleOption);
      return;
    }

    // If only one language is available, auto-select it
    if (visibleCount === 1) {
      this.#selectLanguageOption(firstVisibleOption);
    }
  }

  /**
   * Programmatically select a language option
   * @param {HTMLElement} optionElement - The option element to select
   */
  #selectLanguageOption(optionElement) {
    const languageInput = this.#languageSelect.querySelector('[data-select-input]');
    const button = this.#languageSelect.querySelector('[data-select-button]');
    const allOptions = this.#languageSelect.querySelectorAll('[role="menuitem"]');

    if (!languageInput || !button) return;

    // Remove selection from all options
    allOptions.forEach((option) => option.removeAttribute('data-selected'));

    // Select the new option
    optionElement.setAttribute('data-selected', '');
    languageInput.value = optionElement.dataset.value;

    // Update button content
    button.innerHTML = optionElement.cloneNode(true).innerHTML;

    // Dispatch change event
    languageInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Setup form submission handler to redirect to custom URLs
   */
  #setupFormSubmission() {
    if (!this.#form) return;

    this.#form.addEventListener('submit', (event) => {
      const customUrl = this.#getCustomUrlForSelection();
      if (customUrl) {
        event.preventDefault();
        const absoluteUrl = this.#normalizeUrl(customUrl);
        window.location.href = absoluteUrl;
      }
      // Otherwise let the form submit normally
    });
  }

  /**
   * Normalize URL to ensure it's absolute
   * @param {string} url - The URL to normalize
   * @returns {string} Absolute URL
   */
  #normalizeUrl(url) {
    // If URL already has protocol, return as is
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }

    // If URL starts with //, add https:
    if (url.startsWith('//')) {
      return `https:${url}`;
    }

    // Otherwise, prepend https://
    return `https://${url}`;
  }

  /**
   * Custom URL from the hidden language input value and matching menuitem.
   * @param {HTMLElement} languageList
   * @returns {string|null}
   */
  #getCustomUrlFromLanguageInput(languageList) {
    const languageInput = this.#languageSelect.querySelector('[data-select-input]');
    const selectedLanguageCode = languageInput?.value;
    if (!selectedLanguageCode) return null;

    const selectedOption = Array.from(languageList.querySelectorAll('[role="menuitem"]')).find(
      (option) => option.dataset.value === selectedLanguageCode
    );

    return selectedOption?.dataset.customUrl || null;
  }

  /**
   * Get custom URL for the current country/language selection
   * @returns {string|null} The custom URL or null if not a custom selection
   */
  #getCustomUrlForSelection() {
    const languageList = this.#languageSelect?.querySelector('[data-select-list]');
    if (!languageList) return null;

    // Prefer the marked selection so duplicate locale codes (multiple custom langs) resolve correctly
    const bySelected = languageList.querySelector('[role="menuitem"][data-selected]');
    if (bySelected) {
      return bySelected.dataset.customUrl || null;
    }

    return this.#getCustomUrlFromLanguageInput(languageList);
  }
}

customElements.define('localization-form', LocalizationForm);
